import React, { useMemo, useState } from "react";
import SubTitleNew from "../common/New/SubTitleNew";
import Report from "../manageData/Reports";
import { Box } from "@mui/material";
import { useSearchParams } from "react-router-dom";
import { PAGE_IDS, isJsonDrivenLayoutEnabled } from "../../config/uiLayoutConfig";
import { usePageLayout } from "../../context/LayoutContext";
import { getPageTitleKey } from "../../layout/reportsLayoutUtils";

const RANGE_LABELS = {
  today: "Today",
  yesterday: "Yesterday",
  last_7_days: "Last 7 Days",
  last_30_days: "Last 30 Days",
  this_week: "This Week",
  this_month: "This Month",
};

const mapHeaderDateRange = (payload) => {
  if (!payload) {
    return { selectedDateRange: "today", customDateRange: null };
  }

  if (payload.type === "custom") {
    const startDate = payload.startDate ? new Date(payload.startDate) : null;
    const endDate = payload.endDate ? new Date(payload.endDate) : null;
    if (startDate) startDate.setHours(0, 0, 0, 0);
    if (endDate) endDate.setHours(23, 59, 59, 999);

    return {
      selectedDateRange: "custom",
      customDateRange: {
        startDate,
        endDate,
      },
    };
  }

  const value = payload.value || payload.label;
  const mappedValue =
    value === "last_7_days" || value === "this_week"
      ? "last 7 days"
      : value === "last_30_days" || value === "this_month"
        ? "last_30_days"
        : value || "today";

  return {
    selectedDateRange: mappedValue,
    customDateRange: null,
  };
};

const readDateRange = (searchParams) => {
  const range = searchParams.get("range") || "today";
  if (range === "custom") {
    const from = searchParams.get("from");
    const to = searchParams.get("to");
    if (from && to) {
      return mapHeaderDateRange({ type: "custom", startDate: from, endDate: to });
    }
  }
  return mapHeaderDateRange({
    type: "preset",
    value: RANGE_LABELS[range] ? range : "today",
    label: RANGE_LABELS[range] || "Today",
  });
};

export default function Reports({ school = false } = {}) {
  const [searchParams, setSearchParams] = useSearchParams();
  const orgName = JSON.parse(localStorage.getItem("userState"))?.organization
    ?.name;
  const useJsonLayout = isJsonDrivenLayoutEnabled();
  const reportsPageId = school ? PAGE_IDS.SCHOOL_REPORTS : PAGE_IDS.FLEET_REPORTS;
  const { layout: moduleLayout } = usePageLayout(useJsonLayout ? reportsPageId : null);
  const pageTitle = getPageTitleKey(moduleLayout) || "Reports";
  const [dateRange, setDateRange] = useState(() => readDateRange(searchParams));
  const dateLabel = useMemo(() => {
    const range = searchParams.get("range") || "today";
    if (range === "custom" && searchParams.get("from") && searchParams.get("to")) {
      return `${searchParams.get("from")} - ${searchParams.get("to")}`;
    }
    return RANGE_LABELS[range] || "Today";
  }, [searchParams]);

  const handleDateRangeChange = (payload) => {
    setDateRange(mapHeaderDateRange(payload));
    const next = new URLSearchParams(searchParams);
    if (payload?.type === "custom" && payload.startDate && payload.endDate) {
      next.set("range", "custom");
      next.set("from", payload.startDate);
      next.set("to", payload.endDate);
    } else {
      const value = payload?.value || "today";
      if (!value || value === "today") next.delete("range");
      else next.set("range", value);
      next.delete("from");
      next.delete("to");
    }
    setSearchParams(next, { replace: true });
  };

  return (
    <>
      <SubTitleNew
        title={pageTitle}
        path={`${orgName || "organization"} > Reports `}
        dateLabel={dateLabel}
        onDateRangeChange={handleDateRangeChange}
      />
      <Box
        sx={{
          margin: { xs: "0px 16px", md: "0px 24px" },
          background: "#f4f4f5",
          borderRadius: "8px",
          position: "relative",
        }}
      >
        <Report school={school} dateRange={dateRange} />
      </Box>
    </>
  );
}
